import React, { useState } from "react";
import { T50StrategyRow } from "./T50Card";
import { ChevronDown, ChevronUp, Check, Minus } from "lucide-react";

// Explainable Fela R.I.O confluence card: separate bullish / bearish scores with a per-component breakdown.
export default function ConfluenceCard({ cisd = null, t50 = null, result }) {
  const [open, setOpen] = useState(false);
  if (!result) return null;
  const dominant = result.bias === "bear" ? "bear" : "bull";
  const score = dominant === "bull" ? result.bull : result.bear;
  const color = result.bias === "neutral" ? "text-muted-foreground" : dominant === "bull" ? "text-buy" : "text-sell";
  const bar = result.bias === "neutral" ? "bg-muted-foreground" : dominant === "bull" ? "bg-buy" : "bg-sell";
  const label = score >= 75 ? "Strong confluence" : score >= 50 ? "Moderate" : score >= 25 ? "Weak" : "No setup";

  return (
    <div data-testid="confluence-card" className="absolute top-[92px] md:top-2 right-[64px] md:right-[74px] z-20 w-52 md:w-64 rounded-xl border border-border bg-panel/95 backdrop-blur-md shadow-lg text-xs select-none">
      <button data-testid="confluence-toggle" onClick={() => setOpen((o) => !o)} className="w-full px-3 py-2 flex items-center gap-2 text-left">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold tracking-widest text-primary">FELACOO BOS</span>
            <span className={`text-[10px] font-bold ${color}`} data-testid="confluence-bias">{result.bias === "neutral" ? "NEUTRAL" : dominant === "bull" ? "BULLISH" : "BEARISH"}</span>
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className={`text-2xl font-heading font-bold ${color} font-mono-nums`} data-testid="confluence-score">{score}</span>
            <span className="text-muted-foreground">/ 100 · {label}</span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden"><div className={`h-full ${bar} transition-all`} style={{ width: `${score}%` }} /></div>
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground font-mono-nums">
            <span data-testid="confluence-bull">Bull {result.bull}</span><span data-testid="confluence-bear">Bear {result.bear}</span>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t border-border px-3 py-2 space-y-1" data-testid="confluence-breakdown">
          <T50StrategyRow t50={t50} />
          {cisd && (
            <div className="flex items-center gap-2 pb-1 mb-1 border-b border-border/60" data-testid="confluence-row-cisd" title="Informational — CISD does not alter the 100-point score yet">
              {/CONFIRMED/.test(cisd.state) ? <Check className={`h-3 w-3 ${cisd.state.startsWith("BULLISH") ? "text-buy" : "text-sell"}`} /> : <Minus className="h-3 w-3 text-muted-foreground" />}
              <span className="flex-1">CISD</span>
              <span className="text-muted-foreground truncate max-w-[130px]" data-testid="confluence-cisd-state">{cisd.state.replace(/_/g, " ").toLowerCase()}</span>
              <span className="font-mono-nums w-12 text-right text-muted-foreground">info</span>
            </div>
          )}
          {result.components.map((c) => {
            const pts = dominant === "bull" ? c.bull : c.bear;
            return (
              <div key={c.id} className="flex items-center gap-2" data-testid={`confluence-row-${c.id}`}>
                {pts > 0 ? <Check className="h-3 w-3 text-buy" /> : <Minus className="h-3 w-3 text-muted-foreground" />}
                <span className="flex-1">{c.label}</span>
                <span className="text-muted-foreground truncate max-w-[90px]" title={c.detail}>{c.state}</span>
                <span className={`font-mono-nums w-12 text-right ${pts > 0 ? "text-foreground" : "text-muted-foreground"}`}>+{pts}/{c.max}</span>
              </div>
            );
          })}
          <p className="pt-1 text-[10px] text-muted-foreground">Confluence is a measurement, not a probability. Computed only from data up to the current tick.</p>
        </div>
      )}
    </div>
  );
}
